import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger } from '../components/ui/tabs';
import {
  FileText, Plus, Edit2, Trash2, Upload, Download, CheckCircle2,
  AlertCircle, Search, X as XIcon, Eye, Paperclip, Users as UsersIcon, ShieldCheck, Loader2, Save
} from 'lucide-react';
import { toast } from 'sonner';
import logger from '../utils/logger';
import { format } from 'date-fns';

const CATEGORIES = ['HR', 'Safety', 'Clinical', 'Code of Conduct', 'Operations', 'Training', 'General'];
const CATEGORY_STYLES = {
  HR: 'bg-[#EDE7F6] text-[#6B3FA0] border-[#9B7ED8]',
  Safety: 'bg-[#FFEBEE] text-[#C62828] border-red-300',
  Clinical: 'bg-[#E3F2FD] text-[#1565C0] border-blue-300',
  'Code of Conduct': 'bg-[#FFF8E1] text-[#F57F17] border-yellow-300',
  Operations: 'bg-[#E8F5E9] text-[#1B5E20] border-green-300',
  Training: 'bg-[#FFF3E0] text-[#E65100] border-orange-300',
  General: 'bg-gray-100 text-gray-700 border-gray-300',
};

export default function PoliciesPage() {
  const { api, user } = useAuth();
  const isAdmin = ['administrator', 'supervisor'].includes(user?.role);
  const [policies, setPolicies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState(isAdmin ? 'all' : 'published');
  const [editDialog, setEditDialog] = useState(false);
  const [viewDialog, setViewDialog] = useState(null);
  const [acksDialog, setAcksDialog] = useState(null);
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchPolicies = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/policies');
      setPolicies(data || []);
    } catch (e) { logger.error(e); }
    setLoading(false);
  }, [api]);

  useEffect(() => { fetchPolicies(); }, [fetchPolicies]);

  const filtered = useMemo(() => {
    return (policies || [])
      .filter(p => categoryFilter === 'all' || p.category === categoryFilter)
      .filter(p => statusFilter === 'all' || p.status === statusFilter)
      .filter(p => !search || (p.title || '').toLowerCase().includes(search.toLowerCase()) || (p.summary || '').toLowerCase().includes(search.toLowerCase()));
  }, [policies, categoryFilter, statusFilter, search]);

  const byCategory = useMemo(() => {
    const grouped = {};
    filtered.forEach(p => { (grouped[p.category] = grouped[p.category] || []).push(p); });
    return Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b));
  }, [filtered]);

  const openCreate = () => {
    setForm({
      title: '', category: 'General', version: '1.0', effective_date: format(new Date(), 'yyyy-MM-dd'),
      summary: '', body: '', ack_required: false, status: 'draft',
    });
    setEditDialog(true);
  };

  const openEdit = (p) => { setForm({ ...p }); setEditDialog(true); };

  const savePolicy = async () => {
    if (!form.title?.trim()) { toast.error('Title is required'); return; }
    setSaving(true);
    try {
      if (form.id) {
        await api.put(`/policies/${form.id}`, form);
        toast.success('Policy updated');
      } else {
        await api.post('/policies', form);
        toast.success('Policy created');
      }
      setEditDialog(false);
      fetchPolicies();
    } catch (e) { toast.error('Save failed'); logger.error(e); }
    setSaving(false);
  };

  const deletePolicy = async (id) => {
    if (!window.confirm('Delete this policy and all its acknowledgements?')) return;
    try { await api.delete(`/policies/${id}`); toast.success('Deleted'); fetchPolicies(); }
    catch { toast.error('Delete failed'); }
  };

  const handleUpload = async (policyId, file) => {
    if (!file) return;
    if (!file.type.includes('pdf')) { toast.error('Only PDF files are accepted'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File exceeds 5 MB limit'); return; }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      await api.post(`/policies/${policyId}/upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('PDF attached');
      fetchPolicies();
    } catch (e) { toast.error('Upload failed'); logger.error(e); }
    setUploading(false);
  };

  const removeAttachment = async (policyId) => {
    if (!window.confirm('Remove the PDF attachment?')) return;
    try { await api.delete(`/policies/${policyId}/upload`); toast.success('Attachment removed'); fetchPolicies(); }
    catch { toast.error('Failed'); }
  };

  const downloadAttachment = async (policy) => {
    // Pre-open a blank tab SYNCHRONOUSLY in the click handler so popup-blockers
    // (Chrome/Edge/Safari) treat it as user-initiated. If we wait until after
    // the `await` to call window.open, the browser silently blocks it and the
    // user sees nothing happen.
    const win = window.open('', '_blank');
    if (win) {
      try {
        win.document.write(
          '<title>Loading PDF…</title>' +
          '<style>body{font-family:system-ui,-apple-system,sans-serif;background:#1A1A1A;color:#bbb;display:flex;align-items:center;justify-content:center;height:100vh;margin:0}</style>' +
          '<div>Loading PDF…</div>'
        );
      } catch (_) { /* ignore — some browsers restrict document.write on cross-origin */ }
    }
    try {
      const res = await api.get(`/policies/${policy.id}/download`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: res.data?.type || 'application/pdf' });
      if (!blob.size) {
        if (win) win.close();
        toast.error('Attachment is empty — please re-upload the PDF');
        return;
      }
      const url = URL.createObjectURL(blob);
      if (win && !win.closed) {
        // View inline in the pre-opened tab
        win.location.href = url;
        // Revoke after the tab has had time to load
        setTimeout(() => URL.revokeObjectURL(url), 60000);
      } else {
        // Popup was blocked — fall back to a hidden anchor save (so the user
        // still gets the file even if their browser killed our new tab).
        const a = document.createElement('a');
        a.href = url;
        a.download = policy.attachment_name || `${policy.title || 'policy'}.pdf`;
        a.rel = 'noopener';
        document.body.appendChild(a);
        a.click();
        setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 1000);
        toast.info('Downloaded to your Downloads folder (popup was blocked)');
      }
    } catch (e) {
      if (win && !win.closed) win.close();
      logger.error(e);
      toast.error('Download failed — try re-uploading the PDF');
    }
  };

  const acknowledge = async (policyId) => {
    try { await api.post(`/policies/${policyId}/acknowledge`); toast.success('Acknowledged'); fetchPolicies(); }
    catch { toast.error('Failed'); }
  };

  return (
    <div className="p-4 sm:p-8 space-y-6" data-testid="policies-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-4xl tracking-tight font-extrabold text-[#2D1B4E] flex items-center gap-3" style={{ fontFamily: 'Manrope, sans-serif' }}>
            <ShieldCheck className="h-9 w-9 text-[#6B3FA0]" /> Policies & SOPs
          </h1>
          <p className="text-base text-[#5A4D6E] mt-1">
            {isAdmin ? 'Manage organization policies, procedures, and staff acknowledgements.' : 'Review the policies and procedures for your role.'}
          </p>
        </div>
        {isAdmin && (
          <Button onClick={openCreate} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="add-policy-btn">
            <Plus className="h-4 w-4" /> New Policy
          </Button>
        )}
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[260px] max-w-md">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-[#8E85A0]" />
          <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search policies..." className="pl-9 border-[#DED8EB] bg-white" data-testid="policy-search" />
        </div>
        <select value={categoryFilter} onChange={e => setCategoryFilter(e.target.value)}
          className="border border-[#DED8EB] rounded-md px-3 py-2 text-sm bg-white" data-testid="policy-category-filter">
          <option value="all">All Categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
        {isAdmin && (
          <Tabs value={statusFilter} onValueChange={setStatusFilter}>
            <TabsList className="bg-[#F0EDF6] border border-[#DED8EB]" data-testid="policy-status-tabs">
              <TabsTrigger value="all" className="data-[state=active]:bg-white">All</TabsTrigger>
              <TabsTrigger value="draft" className="data-[state=active]:bg-white">Draft</TabsTrigger>
              <TabsTrigger value="published" className="data-[state=active]:bg-white">Live</TabsTrigger>
              <TabsTrigger value="archived" className="data-[state=active]:bg-white">Archived</TabsTrigger>
            </TabsList>
          </Tabs>
        )}
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-16 text-[#8E85A0]">Loading policies...</div>
      ) : filtered.length === 0 ? (
        <Card className="border-[#DED8EB] shadow-sm"><CardContent className="py-16 text-center text-[#8E85A0]">
          <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-40" />
          <p>{isAdmin ? 'No policies yet. Click "New Policy" to add the first one.' : 'No policies published yet.'}</p>
        </CardContent></Card>
      ) : (
        <div className="space-y-6">
          {byCategory.map(([cat, items]) => (
            <div key={cat} className="space-y-3">
              <h2 className="text-sm font-bold uppercase tracking-wider text-[#6B3FA0] flex items-center gap-2">
                <Badge className={`text-[10px] ${CATEGORY_STYLES[cat] || CATEGORY_STYLES.General}`}>{cat}</Badge>
                <span className="text-[#8E85A0] font-normal">({items.length})</span>
              </h2>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {items.map(p => (
                  <Card key={p.id} className="border-[#DED8EB] shadow-sm hover:border-[#9B7ED8] transition-colors" data-testid={`policy-card-${p.id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-start justify-between gap-3 mb-2">
                        <div className="flex-1 min-w-0">
                          <h3 className="text-sm font-bold text-[#2D1B4E] truncate">{p.title}</h3>
                          <div className="flex items-center gap-2 mt-1 flex-wrap text-[10px] text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                            <span>v{p.version}</span>
                            {p.effective_date && <span>· eff {p.effective_date}</span>}
                            {p.has_attachment && <span className="text-[#6B3FA0] flex items-center gap-0.5"><Paperclip className="h-2.5 w-2.5" />PDF</span>}
                          </div>
                        </div>
                        <div className="flex items-center gap-1 shrink-0">
                          {p.status === 'draft' && <Badge className="text-[9px] bg-gray-100 text-gray-600">Draft</Badge>}
                          {p.status === 'archived' && <Badge className="text-[9px] bg-gray-100 text-gray-500">Archived</Badge>}
                          {p.ack_required && !isAdmin && (p._ack
                            ? <Badge className="text-[9px] bg-[#E8F5E9] text-[#1B5E20] gap-0.5"><CheckCircle2 className="h-2.5 w-2.5" />Acknowledged</Badge>
                            : <Badge className="text-[9px] bg-[#FFEBEE] text-[#C62828] gap-0.5"><AlertCircle className="h-2.5 w-2.5" />Action Needed</Badge>)}
                          {p.ack_required && isAdmin && <Badge className="text-[9px] bg-[#FFF8E1] text-[#F57F17]">Ack Required</Badge>}
                        </div>
                      </div>
                      {p.summary && <p className="text-xs text-[#5A4D6E] line-clamp-2">{p.summary}</p>}
                      <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#F0EDF6]">
                        <Button variant="outline" size="sm" onClick={() => setViewDialog(p)} className="border-[#DED8EB] text-[#6B3FA0] h-7 text-xs gap-1" data-testid={`view-policy-${p.id}`}>
                          <Eye className="h-3 w-3" /> View
                        </Button>
                        <div className="flex items-center gap-1">
                          {p.has_attachment && (
                            <Button variant="ghost" size="icon" onClick={() => downloadAttachment(p)} className="h-7 w-7 text-[#6B3FA0] hover:bg-[#EDE7F6]" title="Open PDF" data-testid={`download-policy-${p.id}`}>
                              <Download className="h-3.5 w-3.5" />
                            </Button>
                          )}
                          {isAdmin && (
                            <>
                              <Button variant="ghost" size="icon" onClick={() => setAcksDialog(p)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" title="Who acknowledged" data-testid={`acks-policy-${p.id}`}>
                                <UsersIcon className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => openEdit(p)} className="h-7 w-7 text-[#8E85A0] hover:text-[#6B3FA0]" data-testid={`edit-policy-${p.id}`}>
                                <Edit2 className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" onClick={() => deletePolicy(p.id)} className="h-7 w-7 text-[#8E85A0] hover:text-[#D9534F]" data-testid={`delete-policy-${p.id}`}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </>
                          )}
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* View Policy Dialog */}
      {viewDialog && (
        <Dialog open onOpenChange={() => setViewDialog(null)}>
          <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="view-policy-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
                <FileText className="h-5 w-5 text-[#6B3FA0]" /> {viewDialog.title}
              </DialogTitle>
              <div className="flex items-center gap-2 flex-wrap pt-2">
                <Badge className={`text-[10px] ${CATEGORY_STYLES[viewDialog.category] || CATEGORY_STYLES.General}`}>{viewDialog.category}</Badge>
                <span className="text-xs text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>
                  v{viewDialog.version}{viewDialog.effective_date ? ` · Effective ${viewDialog.effective_date}` : ''}
                </span>
              </div>
            </DialogHeader>
            <div className="space-y-4 py-2">
              {viewDialog.summary && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Summary</p>
                  <p className="text-sm text-[#2D1B4E]">{viewDialog.summary}</p>
                </div>
              )}
              {viewDialog.body && (
                <div>
                  <p className="text-[10px] font-semibold uppercase tracking-wider text-[#6B3FA0] mb-1">Policy Details</p>
                  <div className="text-sm text-[#5A4D6E] whitespace-pre-line bg-[#F7F4EF] border border-[#DED8EB] rounded-lg p-4">{viewDialog.body}</div>
                </div>
              )}
              {viewDialog.has_attachment && (
                <div className="flex items-center justify-between p-3 rounded-lg border border-[#DED8EB] bg-[#F7F4EF]">
                  <span className="text-sm text-[#2D1B4E] flex items-center gap-2">
                    <Paperclip className="h-4 w-4 text-[#6B3FA0]" />
                    {viewDialog.attachment_name}
                  </span>
                  <Button size="sm" variant="outline" onClick={() => downloadAttachment(viewDialog)} className="border-[#9B7ED8] text-[#6B3FA0] h-8 text-xs gap-1">
                    <Download className="h-3 w-3" /> Open PDF
                  </Button>
                </div>
              )}
            </div>
            <DialogFooter>
              {viewDialog.ack_required && !isAdmin && !viewDialog._ack && (
                <Button onClick={() => { acknowledge(viewDialog.id); setViewDialog(null); }} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="acknowledge-btn">
                  <CheckCircle2 className="h-4 w-4" /> I've Read This
                </Button>
              )}
              {viewDialog.ack_required && !isAdmin && viewDialog._ack && (
                <Badge className="text-sm bg-[#E8F5E9] text-[#1B5E20] gap-1 py-1.5 px-3"><CheckCircle2 className="h-3.5 w-3.5" /> Acknowledged</Badge>
              )}
              <Button variant="outline" onClick={() => setViewDialog(null)}>Close</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Edit Dialog */}
      {editDialog && isAdmin && (
        <Dialog open onOpenChange={setEditDialog}>
          <DialogContent className="sm:max-w-2xl bg-white max-h-[88vh] overflow-y-auto" data-testid="edit-policy-dialog">
            <DialogHeader>
              <DialogTitle className="text-xl font-bold text-[#2D1B4E] flex items-center gap-2">
                <FileText className="h-5 w-5 text-[#6B3FA0]" /> {form.id ? 'Edit' : 'New'} Policy
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-3 py-2">
              <div>
                <Label>Title</Label>
                <Input value={form.title || ''} onChange={e => setForm(p => ({ ...p, title: e.target.value }))} className="border-[#DED8EB]" data-testid="policy-title" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                <div>
                  <Label>Category</Label>
                  <select value={form.category || 'General'} onChange={e => setForm(p => ({ ...p, category: e.target.value }))}
                    className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="policy-category">
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Version</Label>
                  <Input value={form.version || ''} onChange={e => setForm(p => ({ ...p, version: e.target.value }))} placeholder="1.0" className="border-[#DED8EB]" data-testid="policy-version" />
                </div>
                <div>
                  <Label>Effective Date</Label>
                  <Input type="date" value={form.effective_date || ''} onChange={e => setForm(p => ({ ...p, effective_date: e.target.value }))} className="border-[#DED8EB]" data-testid="policy-effective-date" />
                </div>
                <div>
                  <Label>Status</Label>
                  <select value={form.status || 'draft'} onChange={e => setForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full border border-[#DED8EB] rounded-md px-3 py-2 text-sm" data-testid="policy-status">
                    <option value="draft">Draft</option>
                    <option value="published">Live (visible to staff)</option>
                    <option value="archived">Archived</option>
                  </select>
                </div>
              </div>
              <div>
                <Label>Summary <span className="text-xs text-[#8E85A0] font-normal">· shown on the list card</span></Label>
                <Input value={form.summary || ''} onChange={e => setForm(p => ({ ...p, summary: e.target.value }))} placeholder="Brief 1-2 sentence description..." className="border-[#DED8EB]" data-testid="policy-summary" />
              </div>
              <div>
                <Label>Policy Body</Label>
                <Textarea value={form.body || ''} onChange={e => setForm(p => ({ ...p, body: e.target.value }))}
                  className="border-[#DED8EB] min-h-[180px] text-sm" placeholder="Full policy text. Use line breaks for paragraphs; bullets ('- Item') are welcome." data-testid="policy-body" />
              </div>
              <label className="flex items-center gap-2 p-3 rounded-md bg-[#FFF8E1] border border-yellow-200 cursor-pointer">
                <input type="checkbox" checked={!!form.ack_required} onChange={e => setForm(p => ({ ...p, ack_required: e.target.checked }))} className="accent-[#6B3FA0]" data-testid="policy-ack-required" />
                <span className="text-sm text-[#2D1B4E]">Require staff to acknowledge they've read this policy</span>
              </label>
              {form.id && (
                <div className="p-3 rounded-lg border border-[#DED8EB] bg-[#F7F4EF]">
                  <Label className="flex items-center gap-2"><Paperclip className="h-3.5 w-3.5 text-[#6B3FA0]" />PDF Attachment (optional · 5 MB max)</Label>
                  {form.has_attachment ? (
                    <div className="flex items-center justify-between mt-2">
                      <span className="text-xs text-[#5A4D6E]">{form.attachment_name}</span>
                      <div className="flex gap-1">
                        <Button size="sm" variant="outline" onClick={() => downloadAttachment(form)} className="border-[#DED8EB] h-7 text-xs gap-1"><Download className="h-3 w-3" />Open</Button>
                        <Button size="sm" variant="ghost" onClick={async () => { await removeAttachment(form.id); setForm(p => ({ ...p, has_attachment: false, attachment_name: '' })); }} className="h-7 w-7 text-[#D9534F]" data-testid="remove-attachment-btn"><XIcon className="h-3 w-3" /></Button>
                      </div>
                    </div>
                  ) : (
                    <>
                      <input type="file" accept="application/pdf" ref={fileInputRef} onChange={async e => {
                        const f = e.target.files?.[0];
                        if (f) { await handleUpload(form.id, f); await fetchPolicies(); const latest = (policies.find(p => p.id === form.id)); if (latest) setForm(latest); }
                        e.target.value = '';
                      }} className="hidden" data-testid="policy-upload-input" />
                      <Button size="sm" variant="outline" onClick={() => fileInputRef.current?.click()} disabled={uploading} className="mt-2 border-[#9B7ED8] text-[#6B3FA0] gap-1" data-testid="policy-upload-btn">
                        {uploading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Upload className="h-3 w-3" />}
                        {uploading ? 'Uploading...' : 'Upload PDF'}
                      </Button>
                    </>
                  )}
                </div>
              )}
              {!form.id && <p className="text-[11px] text-[#8E85A0] italic">Tip: save the policy first, then you can upload a PDF attachment.</p>}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setEditDialog(false)}>Cancel</Button>
              <Button onClick={savePolicy} disabled={saving || !form.title?.trim()} className="bg-[#6B3FA0] hover:bg-[#5A2F8E] text-white gap-2" data-testid="save-policy-btn">
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                {saving ? 'Saving...' : form.id ? 'Update' : 'Create'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}

      {/* Acknowledgements dialog (admin) */}
      {acksDialog && isAdmin && <AcksDialog policy={acksDialog} close={() => setAcksDialog(null)} api={api} />}
    </div>
  );
}

function AcksDialog({ policy, close, api }) {
  const [data, setData] = useState({ acknowledged: [], missing: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try { const { data } = await api.get(`/policies/${policy.id}/acknowledgements`); setData(data); }
      catch (e) { logger.error(e); }
      setLoading(false);
    })();
  }, [api, policy.id]);

  return (
    <Dialog open onOpenChange={close}>
      <DialogContent className="sm:max-w-lg bg-white max-h-[85vh] overflow-y-auto" data-testid="acks-dialog">
        <DialogHeader>
          <DialogTitle className="text-lg font-bold text-[#2D1B4E]">Acknowledgements · {policy.title}</DialogTitle>
        </DialogHeader>
        {loading ? <div className="text-center py-6 text-[#8E85A0]">Loading...</div> : (
          <div className="space-y-4 py-2">
            <div>
              <p className="text-[10px] font-semibold uppercase tracking-wider text-[#2E7D32] mb-2 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Acknowledged ({data.acknowledged.length})
              </p>
              {data.acknowledged.length === 0 ? <p className="text-xs text-[#8E85A0]">No one has acknowledged yet.</p> :
                data.acknowledged.map(a => (
                  <div key={a.user_id} className="flex items-center justify-between text-sm py-1 border-b border-[#F0EDF6]">
                    <span className="text-[#2D1B4E]">{a.user_name}</span>
                    <span className="text-[10px] text-[#8E85A0]" style={{ fontFamily: 'IBM Plex Mono, monospace' }}>{a.acknowledged_at?.split('T')[0]}</span>
                  </div>
                ))}
            </div>
            {policy.ack_required && (
              <div>
                <p className="text-[10px] font-semibold uppercase tracking-wider text-[#D9534F] mb-2 flex items-center gap-1">
                  <AlertCircle className="h-3 w-3" /> Pending ({data.missing.length})
                </p>
                {data.missing.length === 0 ? <p className="text-xs text-[#2E7D32]">✓ All staff have acknowledged.</p> :
                  data.missing.map(u => (
                    <div key={u.user_id} className="text-sm py-1 border-b border-[#F0EDF6] text-[#5A4D6E]">{u.user_name}</div>
                  ))}
              </div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={close}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
